// ==========================
// TYPES
// ==========================

type NormalizedValue = string | number | boolean | null | undefined;

interface CompareOptions {
  normalize?: boolean;
  ignoreArrayOrder?: boolean;
  ignoreCase?: boolean;
  trimStrings?: boolean;
  treatEmptyAsNull?: boolean;
  ignorePaths?: string[];
  dateCompareMode?: 'full' | 'date' | 'time';
}

interface ChangeDetail {
  path: string;
  type: 
    | 'value_changed' 
    | 'type_changed' 
    | 'date_changed'
    | 'array_length_changed'
    | 'item_added'
    | 'item_removed'
    | 'property_added'
    | 'property_removed'
    | 'structure_changed'
    | 'order_changed';
  oldValue: any;
  newValue: any;
  normalizedOld?: NormalizedValue;
  normalizedNew?: NormalizedValue;
}

// ==========================
// DATE UTILITIES (TIMEZONE-SAFE)
// ==========================

/**
 * Extract date-only string from Date object without timezone conversion
 * Uses local timezone to avoid the Nov 11 -> Nov 10 bug
 */
const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Extract time-only string from Date object
 */
const toLocalTimeString = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${hours}:${minutes}:${seconds}.${ms}Z`;
};

/**
 * Check if a string looks like a date (has date/time separators)
 */
const looksLikeDate = (str: string): boolean => {
  return /[-/T:Z]/.test(str);
};

/**
 * Parse date string safely, preserving local timezone for date-only strings
 */
const parseDateSafely = (str: string, dateCompareMode: string): string | null => {
  // Try to parse as date
  const parsedDate = Date.parse(str);
  if (isNaN(parsedDate)) return null;

  const date = new Date(parsedDate);
  
  // For date-only strings (no time component), use local date extraction
  // This prevents "2025-11-11" from becoming "2025-11-10" due to timezone
  if (!str.includes('T') && !str.includes(':')) {
    switch (dateCompareMode) {
      case 'full':
        return date.toISOString();
      case 'time':
        return toLocalTimeString(date);
      case 'date':
      default:
        return toLocalDateString(date);
    }
  }

  // For full ISO strings, use standard processing
  switch (dateCompareMode) {
    case 'full':
      return date.toISOString();
    case 'time':
      return date.toISOString().split('T')[1];
    case 'date':
    default:
      return date.toISOString().split('T')[0];
  }
};

// ==========================
// SMART NORMALIZATION
// ==========================

/**
 * Intelligently normalize a value for comparison
 * Fixed to handle timezone issues properly
 */
const normalizeValue = (
  value: any, 
  options: CompareOptions = {}
): NormalizedValue => {
  const {
    trimStrings = true,
    ignoreCase = false,
    treatEmptyAsNull = true,
    dateCompareMode = 'date'
  } = options;

  // Handle null/undefined
  if (value == null) return value;

  // If it's already a number, return it as-is
  if (typeof value === "number") {
    if (isNaN(value)) return null;
    if (!isFinite(value)) return value;
    return value;
  }

  // Handle booleans
  if (typeof value === "boolean") return value;

  // Handle Date objects - use local date extraction for date-only mode
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    switch (dateCompareMode) {
      case 'full':
        return value.toISOString();
      case 'time':
        return toLocalTimeString(value);
      case 'date':
      default:
        return toLocalDateString(value);
    }
  }

  // Handle strings
  if (typeof value === "string") {
    let normalized = value;

    // Trim whitespace
    if (trimStrings) {
      normalized = normalized.trim();
    }

    // Treat empty as null
    if (treatEmptyAsNull && normalized === "") {
      return null;
    }

    // Case insensitive
    if (ignoreCase) {
      normalized = normalized.toLowerCase();
    }

    // Check if it's a pure number string (no date separators)
    if (/^-?\d+(\.\d+)?$/.test(normalized)) {
      return Number(normalized);
    }

    // Check if it's a boolean string
    const lowerNorm = normalized.toLowerCase();
    if (lowerNorm === 'true') return true;
    if (lowerNorm === 'false') return false;

    // Check if it looks like a date
    if (looksLikeDate(normalized)) {
      const parsedDate = parseDateSafely(normalized, dateCompareMode);
      if (parsedDate !== null) {
        return parsedDate;
      }
    }

    return normalized;
  }

  return value;
};

/**
 * Check if a path should be ignored
 */
const shouldIgnorePath = (path: string, ignorePaths: string[] = []): boolean => {
  return ignorePaths.some(ignorePath => {
    // Exact match
    if (path === ignorePath) return true;
    
    // Wildcard match (e.g., "*.createdAt")
    if (ignorePath.includes('*')) {
      const regex = new RegExp('^' + ignorePath.replace(/\*/g, '.*') + '$');
      return regex.test(path);
    }
    
    return false;
  });
};

/**
 * Sort array of objects by a key for order-independent comparison
 */
const sortArrayForComparison = (arr: any[]): any[] => {
  if (!Array.isArray(arr)) return arr;
  
  return [...arr].sort((a, b) => {
    // Sort by id if available
    if (a?.id != null && b?.id != null) {
      return String(a.id).localeCompare(String(b.id));
    }
    
    // Sort by uniqueId if available
    if (a?.uniqueId != null && b?.uniqueId != null) {
      return String(a.uniqueId).localeCompare(String(b.uniqueId));
    }
    
    // Sort by JSON stringification as fallback
    return JSON.stringify(a).localeCompare(JSON.stringify(b));
  });
};

// ==========================
// DEEP COMPARE (BOOLEAN)
// ==========================

/**
 * Smart deep comparison - returns true if there are changes
 */
export const deepCompare = (
  obj1: any, 
  obj2: any, 
  options: CompareOptions = {}
): boolean => {
  const {
    normalize = true,
    ignoreArrayOrder = false,
    ignorePaths = []
  } = options;

  // If both are the same reference, they're equal
  if (obj1 === obj2) return false;

  // If either is null or undefined
  if (obj1 == null || obj2 == null) {
    return obj1 !== obj2;
  }

  // Normalize primitive values if enabled
  if (normalize && (typeof obj1 !== "object" || typeof obj2 !== "object")) {
    const norm1 = normalizeValue(obj1, options);
    const norm2 = normalizeValue(obj2, options);
    return norm1 !== norm2;
  }

  // Handle Date objects
  if (obj1 instanceof Date || obj2 instanceof Date) {
    if (normalize) {
      const norm1 = normalizeValue(obj1, options);
      const norm2 = normalizeValue(obj2, options);
      return norm1 !== norm2;
    }
    if (obj1 instanceof Date && obj2 instanceof Date) {
      return obj1.getTime() !== obj2.getTime();
    }
    return true;
  }

  // Handle Arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return true;

    // Sort arrays if order should be ignored
    const arr1 = ignoreArrayOrder ? sortArrayForComparison(obj1) : obj1;
    const arr2 = ignoreArrayOrder ? sortArrayForComparison(obj2) : obj2;

    for (let i = 0; i < arr1.length; i++) {
      if (deepCompare(arr1[i], arr2[i], options)) {
        return true;
      }
    }
    return false;
  }

  // One is array, other is not
  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return true;
  }

  // Handle Objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Filter out ignored paths
  const filteredKeys1 = keys1.filter(key => !shouldIgnorePath(key, ignorePaths));
  const filteredKeys2 = keys2.filter(key => !shouldIgnorePath(key, ignorePaths));

  if (filteredKeys1.length !== filteredKeys2.length) return true;

  // Check all keys and values
  for (const key of filteredKeys1) {
    if (!filteredKeys2.includes(key)) return true;
    
    if (shouldIgnorePath(key, ignorePaths)) continue;
    
    if (deepCompare(obj1[key], obj2[key], options)) {
      return true;
    }
  }

  return false;
};

// ==========================
// DEEP COMPARE WITH DETAILS
// ==========================

/**
 * Deep comparison with detailed change tracking
 */
export const deepCompareWithDetails = (
  obj1: any,
  obj2: any,
  path: string = "",
  options: CompareOptions = {}
): ChangeDetail[] => {
  const {
    normalize = true,
    ignoreArrayOrder = false,
    ignorePaths = []
  } = options;

  const changes: ChangeDetail[] = [];

  // Skip ignored paths
  if (shouldIgnorePath(path, ignorePaths)) {
    return changes;
  }

  // If both are the same reference
  if (obj1 === obj2) return changes;

  // Normalize primitives if enabled
  if (normalize && (typeof obj1 !== "object" || typeof obj2 !== "object")) {
    const norm1 = normalizeValue(obj1, options);
    const norm2 = normalizeValue(obj2, options);
    if (norm1 !== norm2) {
      changes.push({
        path: path || "root",
        oldValue: obj1,
        newValue: obj2,
        normalizedOld: norm1,
        normalizedNew: norm2,
        type: typeof obj1 !== typeof obj2 ? "type_changed" : "value_changed",
      });
    }
    return changes;
  }

  // If either is null or undefined
  if (obj1 == null || obj2 == null) {
    if (obj1 !== obj2) {
      changes.push({
        path: path || "root",
        oldValue: obj1,
        newValue: obj2,
        type: "value_changed",
      });
    }
    return changes;
  }

  // Handle Date objects
  if (obj1 instanceof Date || obj2 instanceof Date) {
    if (normalize) {
      const norm1 = normalizeValue(obj1, options);
      const norm2 = normalizeValue(obj2, options);
      if (norm1 !== norm2) {
        changes.push({
          path,
          oldValue: obj1,
          newValue: obj2,
          normalizedOld: norm1,
          normalizedNew: norm2,
          type: "date_changed",
        });
      }
    } else if (
      obj1 instanceof Date &&
      obj2 instanceof Date &&
      obj1.getTime() !== obj2.getTime()
    ) {
      changes.push({
        path,
        oldValue: obj1,
        newValue: obj2,
        type: "date_changed",
      });
    }
    return changes;
  }

  // Handle Arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      changes.push({
        path,
        oldValue: obj1.length,
        newValue: obj2.length,
        type: "array_length_changed",
      });
    }

    // Sort arrays if order should be ignored
    const arr1 = ignoreArrayOrder ? sortArrayForComparison(obj1) : obj1;
    const arr2 = ignoreArrayOrder ? sortArrayForComparison(obj2) : obj2;

    const maxLength = Math.max(arr1.length, arr2.length);
    for (let i = 0; i < maxLength; i++) {
      const newPath = `${path}[${i}]`;
      
      if (shouldIgnorePath(newPath, ignorePaths)) continue;
      
      if (i >= arr1.length) {
        changes.push({
          path: newPath,
          oldValue: undefined,
          newValue: arr2[i],
          type: "item_added",
        });
      } else if (i >= arr2.length) {
        changes.push({
          path: newPath,
          oldValue: arr1[i],
          newValue: undefined,
          type: "item_removed",
        });
      } else {
        changes.push(...deepCompareWithDetails(arr1[i], arr2[i], newPath, options));
      }
    }
    return changes;
  }

  // Handle Objects
  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    changes.push({
      path,
      oldValue: obj1,
      newValue: obj2,
      type: "structure_changed",
    });
    return changes;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  const allKeys = new Set([...keys1, ...keys2]);

  for (const key of allKeys) {
    const newPath = path ? `${path}.${key}` : key;
    
    if (shouldIgnorePath(newPath, ignorePaths)) continue;

    if (!(key in obj1)) {
      changes.push({
        path: newPath,
        oldValue: undefined,
        newValue: obj2[key],
        type: "property_added",
      });
    } else if (!(key in obj2)) {
      changes.push({
        path: newPath,
        oldValue: obj1[key],
        newValue: undefined,
        type: "property_removed",
      });
    } else {
      changes.push(...deepCompareWithDetails(obj1[key], obj2[key], newPath, options));
    }
  }

  return changes;
};

// ==========================
// DEBUG HELPER
// ==========================

export const debugCompare = (
  obj1: any,
  obj2: any,
  maxChanges: number = 10,
  options: CompareOptions = {}
): ChangeDetail[] => {
  const changes = deepCompareWithDetails(obj1, obj2, "", options);

  console.group("Smart Deep Compare Debug (Timezone-Fixed)");
  console.log("Options:", options);
  console.log("Has changes:", changes.length > 0);
  console.log("Total changes found:", changes.length);

  if (changes.length > 0) {
    console.log("\n First", Math.min(maxChanges, changes.length), "changes:");
    changes.slice(0, maxChanges).forEach((change, i) => {
      console.log(`\n${i + 1}. ${change.type} at "${change.path}"`);
      console.log("   Old:", change.oldValue);
      console.log("   New:", change.newValue);
      if (change.normalizedOld !== undefined) {
        console.log("Normalized Old:", change.normalizedOld);
        console.log("Normalized New:", change.normalizedNew);
      }
    });

    if (changes.length > maxChanges) {
      console.log(`\n... and ${changes.length - maxChanges} more changes`);
    }
  } else {
    console.log("Objects are identical!");
  }

  console.groupEnd();

  return changes;
};

// ==========================
// UTILITY: Date Formatting Helper
// ==========================

/**
 * Helper function to format dates consistently for storage/comparison
 * Use this in your UI components to avoid timezone issues
 */
export const formatDateForStorage = (date: Date | string): string => {
  if (typeof date === 'string') {
    // If already a date-only string, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse and format
    date = new Date(date);
  }
  
  return toLocalDateString(date);
};

/**
 * Example usage in your React components:
 * 
 * // When handling date picker changes:
 * const handleDateChange = (selectedDate) => {
 *   const dateOnly = formatDateForStorage(selectedDate);
 *   setScheduledAt(dateOnly); // Stores as "2025-11-11"
 * };
 * 
 * // This prevents the Nov 11 -> Nov 10 timezone bug!
 */

// ==========================
// EXTRACT NEW/CHANGED DATA
// ==========================

interface ExtractNewDataOptions extends CompareOptions {
  includeModified?: boolean;      // Include properties that changed values
  includeAdded?: boolean;          // Include newly added properties
  includeArrayChanges?: boolean;   // Include array items that were added/modified
  deepExtract?: boolean;           // Extract nested changes recursively
  specialArrayKeys?: string[];     // Keys for which we return the full updated array (e.g. ['medications','labTests'])
}

interface ExtractedData {
  added: Record<string, any>;      // Completely new properties
  modified: Record<string, any>;   // Changed properties with new values
  combined: Record<string, any>;   // All changes combined (added + modified)
  changeCount: number;             // Total number of changes
  paths: string[];                 // List of all changed paths
}

/**
 * Extracts only new or changed data from comparing old and new objects
 * Returns a filtered object containing only the differences
 */
export const extractNewData = (
  oldData: any,
  newData: any,
  options: ExtractNewDataOptions = {}
): ExtractedData => {
  const {
    includeModified = true,
    includeAdded = true,
    includeArrayChanges = true,
    deepExtract = true,
    specialArrayKeys = ['medications', 'labTests'],
    ...compareOptions
  } = options;

  // Get all changes using existing comparison function
  const changes = deepCompareWithDetails(oldData, newData, "", compareOptions);

  const added: Record<string, any> = {};
  const modified: Record<string, any> = {};
  const paths: string[] = [];
  const handledSpecials = new Set<string>(); // track special keys already processed

  // Helper to check if path belongs to a special array key
  const pathBelongsToSpecial = (path: string): string | null => {
    if (!path) return null;
    for (const key of specialArrayKeys) {
      if (
        path === key ||
        path.startsWith(`${key}.`) ||
        path.startsWith(`${key}[`)
      ) {
        return key;
      }
    }
    return null;
  };

  // Process each change
  changes.forEach(change => {
    paths.push(change.path);

    // If this change is part of a special array (e.g. medications, labTests),
    // record the full updated array and skip per-item extraction.
    const specialKey = pathBelongsToSpecial(change.path);
    if (specialKey) {
      if (!handledSpecials.has(specialKey) && includeArrayChanges) {
        // Put the full updated array under the root key
        const fullArr = getNestedValue(newData, specialKey);
        if (fullArr !== undefined) {
          setNestedValue(modified, specialKey, fullArr);
        }
        handledSpecials.add(specialKey);
      }
      return; // skip detailed handling for this change
    }

    // Handle added properties
    if (change.type === 'property_added' || change.type === 'item_added') {
      if (includeAdded) {
        setNestedValue(added, change.path, change.newValue);
      }
    }

    // Handle modified values
    if (
      includeModified &&
      (change.type === 'value_changed' ||
        change.type === 'type_changed' ||
        change.type === 'date_changed')
    ) {
      setNestedValue(modified, change.path, change.newValue);
    }

    // Handle array length changes for non-special arrays
    if (includeArrayChanges && change.type === 'array_length_changed') {
      const arrayPath = change.path;
      const newArray = getNestedValue(newData, arrayPath);
      if (Array.isArray(newArray)) {
        setNestedValue(modified, arrayPath, newArray);
      }
    }
  });

  // Combine added and modified
  const combined = deepMerge(added, modified);

  return {
    added,
    modified,
    combined,
    changeCount: changes.length,
    paths
  };
};

/**
 * Helper: Set a value in nested object using dot/bracket notation path
 */
const setNestedValue = (obj: any, path: string, value: any): void => {
  const parts = parsePath(path);
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];

    // Determine if next level should be array or object
    const isNextArray = typeof nextPart === 'number';

    if (!(part in current)) {
      current[part] = isNextArray ? [] : {};
    }

    current = current[part];
  }

  const lastPart = parts[parts.length - 1];
  current[lastPart] = value;
};

/**
 * Helper: Get a value from nested object using dot/bracket notation path
 */
const getNestedValue = (obj: any, path: string): any => {
  const parts = parsePath(path);
  let current = obj;

  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }

  return current;
};

/**
 * Helper: Parse path string into array of keys (handling arrays)
 */
const parsePath = (path: string): (string | number)[] => {
  if (!path) return [];

  const parts: (string | number)[] = [];
  const segments = path.split('.');

  for (const segment of segments) {
    // Handle array notation: "items[0]" -> ["items", 0]
    const arrayMatch = segment.match(/^(.+?)\[(\d+)\]$/);
    if (arrayMatch) {
      parts.push(arrayMatch[1]);
      parts.push(parseInt(arrayMatch[2], 10));
    } else {
      parts.push(segment);
    }
  }

  return parts;
};

/**
 * Helper: Deep merge two objects
 */
const deepMerge = (target: any, source: any): any => {
  if (!source) return target;
  if (!target) return source;

  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        !Array.isArray(source[key]) &&
        !(source[key] instanceof Date)
      ) {
        result[key] = deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
};

// ==========================
// SIMPLIFIED API
// ==========================

/**
 * Quick function: Get only new/added properties
 */
export const getAddedProperties = (
  oldData: any,
  newData: any,
  options: CompareOptions = {}
): any => {
  const result = extractNewData(oldData, newData, {
    ...options,
    includeAdded: true,
    includeModified: false,
    includeArrayChanges: false
  });
  return result.added;
};

/**
 * Quick function: Get only modified properties
 */
export const getModifiedProperties = (
  oldData: any,
  newData: any,
  options: CompareOptions = {}
): any => {
  const result = extractNewData(oldData, newData, {
    ...options,
    includeAdded: false,
    includeModified: true,
    includeArrayChanges: true
  });
  return result.modified;
};

/**
 * Quick function: Get all changes (added + modified)
 */
export const getAllChanges = (
  oldData: any,
  newData: any,
  options: CompareOptions = {}
): any => {
  const result = extractNewData(oldData, newData, {
    ...options,
    includeAdded: true,
    includeModified: true,
    includeArrayChanges: true
  });
  return result.combined;
};

// ==========================
// USAGE EXAMPLES
// ==========================

/**
 * Example 1: Basic usage
 * 
 * const oldUser = {
 *   name: "John",
 *   age: 30,
 *   email: "john@example.com"
 * };
 * 
 * const newUser = {
 *   name: "John",
 *   age: 31,
 *   email: "john@example.com",
 *   phone: "+1234567890"
 * };
 * 
 * const changes = extractNewData(oldUser, newUser);
 * console.log(changes.added);    // { phone: "+1234567890" }
 * console.log(changes.modified); // { age: 31 }
 * console.log(changes.combined); // { age: 31, phone: "+1234567890" }
 */

/**
 * Example 2: Nested objects
 * 
 * const oldOrder = {
 *   id: 1,
 *   customer: { name: "Alice", email: "alice@test.com" },
 *   items: [{ id: 1, qty: 2 }]
 * };
 * 
 * const newOrder = {
 *   id: 1,
 *   customer: { name: "Alice", email: "alice@test.com", phone: "123" },
 *   items: [{ id: 1, qty: 5 }, { id: 2, qty: 1 }]
 * };
 * 
 * const changes = extractNewData(oldOrder, newOrder);
 * // Returns: { customer: { phone: "123" }, items: [...] }
 */

/**
 * Example 3: With normalization options
 * 
 * const oldData = { scheduledAt: "2025-11-11T00:00:00.000Z" };
 * const newData = { scheduledAt: "2025-11-11" };
 * 
 * const changes = extractNewData(oldData, newData, {
 *   normalize: true,
 *   dateCompareMode: 'date'
 * });
 * // No changes detected (same date, different format)
 */

/**
 * Example 4: Quick helpers
 * 
 * const onlyNew = getAddedProperties(oldData, newData);
 * const onlyModified = getModifiedProperties(oldData, newData);
 * const allChanges = getAllChanges(oldData, newData);
 */

